import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SyncIcon from "@material-ui/icons/Sync";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import ConfirmationModal from "../../components/ConfirmationModal";
import KnowledgeArticleModal from "../../components/KnowledgeArticleModal";
import useSocketSubscription from "../../hooks/useSocketSubscription";
import {
  CATEGORY_LABELS,
  INDEX_STATUS_LABELS,
  INDEX_STATUS_TONE,
} from "./constants";

const useStyles = makeStyles((theme) => ({
  search: { minWidth: 280 },
  filter: { minWidth: 160 },
  titleCell: { display: "flex", flexDirection: "column", gap: 1 },
  muted: { color: theme.palette.text.hint },
  secondary: { color: theme.palette.text.secondary },
  playground: {
    margin: theme.spacing(0, 0, 3),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  playgroundRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
  },
  hit: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
  },
  hitHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  hitBody: {
    color: theme.palette.text.secondary,
    whiteSpace: "pre-wrap",
    maxHeight: 120,
    overflow: "hidden",
  },
  score: {
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.text.secondary,
  },
}));

const KnowledgeBase = () => {
  const classes = useStyles();

  const [articles, setArticles] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [category, setCategory] = useState("");
  const [indexStatus, setIndexStatus] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [reindexingAll, setReindexingAll] = useState(false);

  const [testQuery, setTestQuery] = useState("");
  const [testing, setTesting] = useState(false);
  const [hits, setHits] = useState(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/knowledge-base", {
        params: {
          searchParam: searchParam || undefined,
          category: category || undefined,
          pageSize: 100,
        },
      });
      const rows = data.articles || [];
      setArticles(
        indexStatus ? rows.filter((a) => a.indexStatus === indexStatus) : rows
      );
      setCount(data.count || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [searchParam, category, indexStatus]);

  useEffect(() => {
    const t = setTimeout(fetchArticles, 350);
    return () => clearTimeout(t);
  }, [fetchArticles]);

  // A indexação é assíncrona do ponto de vista da UI — o socket evita que o
  // usuário fique recarregando para saber se terminou.
  useSocketSubscription("knowledge-article", fetchArticles);

  const openCreate = () => {
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (id) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const handleReindex = async (article) => {
    try {
      const { data } = await api.post(
        `/knowledge-base/${article.id}/reindex`,
        {}
      );
      toast.success(
        data.skipped
          ? "Conteúdo não mudou — nada foi reindexado."
          : `Indexado em ${data.chunks} ${data.chunks === 1 ? "trecho" : "trechos"}.`
      );
      fetchArticles();
    } catch (err) {
      toastError(err);
    }
  };

  const handleReindexAll = async () => {
    setReindexingAll(true);
    try {
      const { data } = await api.post("/knowledge-base/reindex-all", {});
      toast.success(
        `${data.indexed} indexado(s), ${data.skipped} sem mudança, ${data.failed} com erro.`
      );
      fetchArticles();
    } catch (err) {
      toastError(err);
    } finally {
      setReindexingAll(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/knowledge-base/${deleting.id}`);
      toast.success("Artigo removido");
      fetchArticles();
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
  };

  const handleTest = async () => {
    if (testQuery.trim().length < 2) return;
    setTesting(true);
    try {
      const { data } = await api.post("/knowledge-base/search", {
        query: testQuery,
      });
      setHits(data.hits || []);
    } catch (err) {
      toastError(err);
      setHits([]);
    } finally {
      setTesting(false);
    }
  };

  const pendingCount = articles.filter(
    (a) => a.indexStatus === "pending" || a.indexStatus === "error"
  ).length;

  const dash = <span className={classes.muted}>—</span>;

  const columns = [
    {
      key: "title",
      label: "Título",
      render: (a) => (
        <div className={classes.titleCell}>
          <Typography variant="subtitle2">{a.title}</Typography>
          <Typography variant="caption" className={classes.secondary}>
            {(a.content || "").slice(0, 90)}
            {(a.content || "").length > 90 ? "…" : ""}
          </Typography>
        </div>
      ),
    },
    {
      key: "category",
      label: "Categoria",
      render: (a) => (
        <StatusChip tone="neutral" label={CATEGORY_LABELS[a.category] || a.category} />
      ),
    },
    {
      key: "queues",
      label: "Filas",
      render: (a) =>
        a.queues?.length ? (
          a.queues.map((q) => q.name).join(", ")
        ) : (
          <span className={classes.secondary}>Todas</span>
        ),
    },
    {
      key: "status",
      label: "Publicação",
      render: (a) => (
        <StatusChip
          dot
          tone={a.status === "active" ? "success" : "neutral"}
          label={a.status === "active" ? "Ativo" : "Rascunho"}
        />
      ),
    },
    {
      key: "indexStatus",
      label: "Índice",
      render: (a) => (
        <Tooltip title={a.indexError || ""}>
          <span>
            <StatusChip
              dot
              tone={INDEX_STATUS_TONE[a.indexStatus] || "neutral"}
              label={INDEX_STATUS_LABELS[a.indexStatus] || a.indexStatus}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      key: "chunkCount",
      label: "Trechos",
      type: "numeric",
      render: (a) => a.chunkCount || dash,
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: (a) => (
        <>
          <Tooltip title="Reindexar">
            <IconButton size="small" onClick={() => handleReindex(a)}>
              <SyncIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => openEdit(a.id)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" onClick={() => setDeleting(a)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      <KnowledgeArticleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        articleId={editingId}
        onSaved={fetchArticles}
      />
      <ConfirmationModal
        title={deleting ? `Excluir "${deleting.title}"?` : ""}
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        O artigo e seus trechos vetorizados serão removidos. A IA deixa de
        considerar este conteúdo imediatamente.
      </ConfirmationModal>

      <PageHeader
        title="Base de Conhecimento"
        count={count}
        subtitle="Conteúdo que a IA consulta antes de responder — FAQ, scripts, políticas e informações de produto."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={
                reindexingAll ? (
                  <CircularProgress size={16} />
                ) : (
                  <SyncIcon />
                )
              }
              onClick={handleReindexAll}
              disabled={reindexingAll || !pendingCount}
            >
              Indexar pendentes{pendingCount ? ` (${pendingCount})` : ""}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openCreate}
            >
              Novo artigo
            </Button>
          </>
        }
        filters={
          <>
            <TextField
              className={classes.search}
              placeholder="Buscar por título ou conteúdo"
              type="search"
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Categoria"
              className={classes.filter}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Índice"
              className={classes.filter}
              value={indexStatus}
              onChange={(e) => setIndexStatus(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(INDEX_STATUS_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
      />

      <Paper variant="outlined" className={classes.playground}>
        <Typography variant="subtitle2" gutterBottom>
          Testar a busca
        </Typography>
        <Typography variant="caption" className={classes.secondary}>
          Escreva uma pergunta como o cliente escreveria e veja quais trechos a
          IA receberia. Não consome chamada de chat.
        </Typography>
        <div className={classes.playgroundRow} style={{ marginTop: 12 }}>
          <TextField
            fullWidth
            placeholder="Ex.: vocês parcelam em quantas vezes?"
            value={testQuery}
            onChange={(e) => setTestQuery(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleTest()}
          />
          <Button
            variant="outlined"
            startIcon={testing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            onClick={handleTest}
            disabled={testing || testQuery.trim().length < 2}
          >
            Testar
          </Button>
        </div>

        {hits !== null && !hits.length && (
          <Typography variant="body2" className={classes.secondary} style={{ marginTop: 12 }}>
            Nenhum trecho passou do limiar de similaridade. A IA responderia sem
            contexto da base.
          </Typography>
        )}

        {(hits || []).map((hit, i) => (
          <div key={hit.chunkId} className={classes.hit}>
            <div className={classes.hitHeader}>
              <Typography variant="subtitle2">
                {i + 1}. {hit.articleTitle}
              </Typography>
              <span className={classes.score}>
                similaridade {(hit.score * 100).toFixed(0)}%
              </span>
            </div>
            <Typography variant="body2" className={classes.hitBody}>
              {hit.content}
            </Typography>
          </div>
        ))}
      </Paper>

      <DataTable
        columns={columns}
        rows={articles}
        loading={loading}
        emptyIcon={<MenuBookIcon />}
        emptyTitle={
          searchParam || category || indexStatus
            ? "Nenhum artigo encontrado"
            : "Base de conhecimento vazia"
        }
        emptyDescription={
          searchParam || category || indexStatus
            ? "Ajuste a busca ou os filtros para ver outros artigos."
            : "Sem base, a IA responde só com o histórico da conversa e inventa dado de produto. Comece cadastrando o FAQ."
        }
        emptyAction={
          !searchParam && !category && !indexStatus ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openCreate}
            >
              Novo artigo
            </Button>
          ) : null
        }
      />
    </MainContainer>
  );
};

export default KnowledgeBase;
